import { toast } from 'react-toastify';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';

import { generalSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useAssignUserTeamMutation,
  useBuildingsShortQuery,
  useEditUserMutation,
  useOrganizationsShortQuery,
  usePartnerShortQuery,
  useProvidersShortQuery,
  useRolesQuery,
  useTeamsShortQuery,
  useTilesQuery,
  useTransferTeamMemberMutation,
  useUserListShortQuery,
} from '@common/cache';
import { components, paths, USER_SUFFIX } from '@common/types';
import { t } from 'i18next';
import { isEqual, omit } from 'lodash';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useContext, useEffect, useState } from 'react';
import {
  MAXIMUM_NUMBER_OF_USER_ROLES,
  ROOT_ORGANIZATION_ID,
} from '@common/types/constants.ts';
import { AdminPanelDrawerContext } from '@views/pages/Admin/context';

type RoleAssignment = NonNullable<
  components['schemas']['UpdateUserRequest']['new_role_assignments']
>[number];

type RoleAssignmentType =
  | ''
  | 'super_admin'
  | 'organization_admin'
  | 'partner_admin'
  | 'user_admin'
  | 'wellness_coordinator'
  | 'care_navigator'
  | 'care_manager'
  | 'pharmacist'
  | 'data_analyst'
  | 'reporter'
  | 'provider'
  | 'quality_assurance'
  | 'organization_supervisor'
  | 'operations_coordinator'
  | 'partner_supervisor'
  | 'buildings_manager'
  | 'executive_reporter';

const getRoleAssignmentHash = (roleAssignment: {
  role: RoleAssignmentType;
  record_id: string;
}) => `${roleAssignment.role}:${roleAssignment.record_id}`;

interface IGeneralFormShape {
  first_name: string;
  last_name: string;
  suffix?: USER_SUFFIX | null;
  street?: string | null;
  suite?: string | null;
  city?: string | null;
  state?: string | null;
  county?: string | null;
  zip_code?: string | null;
  phone_number?: string | null;
  mobile_phone_number?: string | null;
  team_id?: string;
  roles?: {
    role: RoleAssignmentType;
    record_id: string;
  }[];
  title?: string | null;
  supervisor_id?: string;
}

const initialValues: IGeneralFormShape = {
  first_name: '',
  last_name: '',
  street: '',
  suite: '',
  city: '',
  state: '',
  county: '',
  zip_code: '',
  phone_number: '',
  mobile_phone_number: '',
  team_id: '',
  roles: [],
  title: null,
  supervisor_id: '',
};

const getDefaultValues = (
  user: paths[`/user/{user_id}`]['get']['responses']['200']['content']['application/json'],
) => ({
  ...initialValues,
  ...omit(user, ['country', 'state']),
  ...user.address,
  suffix: user.suffix as USER_SUFFIX,
  team_id: user.team?.id || '',
  roles: user.assignments?.map(assignment => ({
    role: assignment.role.name,
    record_id: assignment.record.id,
  })),
  supervisor_id: user.supervisor?.id,
});

export const useGeneral = (
  user: paths[`/user/{user_id}`]['get']['responses']['200']['content']['application/json'] & {
    id: string;
  },
) => {
  const {
    triggerClose,
    triggerTabChange,
    setIsFormClean,
    handleCloseConfirmationModal,
  } = useContext(AdminPanelDrawerContext);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const { mutateAsync, isPending } = useEditUserMutation(user.id);
  const transferTeamMemberMutation = useTransferTeamMemberMutation();
  const assignUserToTeamMutation = useAssignUserTeamMutation({
    id: user.team?.id || '',
  });
  const { data: titles = [] } = useTilesQuery();
  const { data: roleOptions = [] } = useRolesQuery();
  const { data: teamOptions = [] } = useTeamsShortQuery({});
  const { data: buildingOptions = [] } = useBuildingsShortQuery({});
  const { data: providerOptions = [] } = useProvidersShortQuery({});
  const { data: organizationOptions = [] } = useOrganizationsShortQuery();
  const { data: partnerOptions = [] } = usePartnerShortQuery();
  const { data: supervisorOptions = [] } = useUserListShortQuery({
    roles: ['organization_supervisor', 'partner_supervisor'],
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isSubmitting, isDirty, errors, isValid, dirtyFields },
    trigger,
    setValue,
  } = useForm<IGeneralFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IGeneralFormShape, any, any>(generalSchema),
    defaultValues: getDefaultValues(user),
  });

  const { prepend, remove, fields } = useFieldArray({ control, name: 'roles' });

  const handleAddRole = () => {
    if (fields.length < MAXIMUM_NUMBER_OF_USER_ROLES) {
      prepend({ role: '', record_id: '' });
      trigger(`roles`);
    }
  };

  const handleRemoveRole = (index: number) => {
    remove(index);
    trigger(`roles`);
  };

  const roleFields = useWatch({ control, name: 'roles' }) || [];

  const isAddRoleBtnDisabled =
    roleFields.length >= MAXIMUM_NUMBER_OF_USER_ROLES ||
    !!roleFields.find(role => !role.role || !role.record_id);

  const getRoleOptions = (
    role?: paths[`/user/roles`]['get']['responses']['200']['content']['application/json'][0],
  ) => {
    if (!role) {
      return [{ id: 'role_not_found', name: 'Role not found' }];
    }

    const { record_type } = role;

    switch (record_type) {
      case 'partner':
        return partnerOptions;
      case 'organization':
        return organizationOptions;
      case 'provider':
        return providerOptions;
      case 'building':
        return buildingOptions;
      case 'root':
        return [{ id: ROOT_ORGANIZATION_ID, name: 'All Organizations' }];
      default:
        return [{ id: 'type_not_found', name: 'Type not found' }];
    }
  };

  const getRoleRecordLabel = (
    role?: paths[`/user/roles`]['get']['responses']['200']['content']['application/json'][0],
  ) => {
    if (!role) {
      return 'Role not found';
    }

    switch (role.record_type) {
      case 'partner':
        return t('adminPanel.users.addModal.dynamicRecordLabels.partner');
      case 'organization':
        return t('adminPanel.users.addModal.dynamicRecordLabels.organization');
      case 'provider':
        return t('adminPanel.users.addModal.dynamicRecordLabels.provider');
      case 'building':
        return t('adminPanel.users.addModal.dynamicRecordLabels.building');
    }
  };

  const submitHandler = async (values: IGeneralFormShape) => {
    try {
      const existingRolesMap = new Map(
        user.assignments?.map(role => [
          getRoleAssignmentHash({
            role: role.role.name,
            record_id: role.record.id,
          }),
          role,
        ]) || [],
      );
      const existingRolesSet = new Set(existingRolesMap.keys());
      const valuesRolesSet = new Set(
        values.roles?.map(role => getRoleAssignmentHash(role)) || [],
      );

      const addedRoleAssignments = values.roles
        ? values.roles.filter(
            roleEntry =>
              !existingRolesSet.has(getRoleAssignmentHash(roleEntry)),
          )
        : [];
      const removedRoleAssignments = user.assignments
        ? user.assignments
            .filter(
              assignment =>
                !valuesRolesSet.has(
                  getRoleAssignmentHash({
                    role: assignment.role.name,
                    record_id: assignment.record.id,
                  }),
                ),
            )
            .map<RoleAssignment>(role => ({
              record_id: role.record.id,
              role: role.role.name,
            }))
        : [];

      if (user.team && values.team_id && user.team?.id !== values.team_id) {
        await transferTeamMemberMutation.mutateAsync({
          team_id: user.team.id,
          team2_id: values.team_id,
          user_ids: [user.id],
        });
      } else if (!user.team && values.team_id) {
        await assignUserToTeamMutation.mutateAsync({
          team_id: values.team_id,
          user_ids: [user.id],
        });
      }

      await mutateAsync({
        ...omit(values, 'roles', 'assignments', 'team', 'team_id', 'title'),
        user_id: user.id as never,
        new_role_assignments:
          addedRoleAssignments as components['schemas']['UpdateUserRequest']['new_role_assignments'],
        removed_role_assignments: removedRoleAssignments,
        supervisor_id: values.supervisor_id ? values.supervisor_id : null,
        title:
          values.title as paths[`/user/{user_id}`]['patch']['requestBody']['content']['application/json']['title'],
      });

      toast.success('Changes saved');
      reset(values);
      setIsFormClean(true);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    handleCloseConfirmationModal();
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsFormClean(true);
  };

  const handleSaveChanges = async () => {
    onSubmit();
    reset(getValues());
    setIsFormClean(true);
  };

  useEffect(() => {
    if (triggerClose && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (triggerTabChange && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (isDirty && Object.keys(dirtyFields).length > 0) {
      setIsFormClean(false);
      return;
    }

    if (triggerClose && !isDirty) {
      setIsFormClean(true);
    }

    if (triggerTabChange && !isDirty) {
      setIsFormClean(true);
    }
  }, [triggerClose, isDirty, triggerTabChange]);

  const duplicateRole = roleFields.find((role, roleIdx) =>
    roleFields.some(
      (secondRole, secondRoleIdx) =>
        isEqual(role, secondRole) && roleIdx !== secondRoleIdx,
    ),
  );

  return {
    control,
    onSubmit,
    errors,
    roleFields,
    roleFieldIds: fields,
    isSubmitEnabled:
      isDirty &&
      Object.keys(dirtyFields).length > 0 &&
      Object.keys(errors).length === 0 &&
      !isSubmitting,
    isSubmitting,
    teamOptions: teamOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    roleOptions,
    titles: titles.map(o => ({
      value: o,
      label: o,
    })),
    supervisorOptions: supervisorOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    handleAddRole,
    handleRemoveRole,
    getRoleOptions,
    getRoleRecordLabel,
    isAddRoleBtnDisabled,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
    setValue,
    duplicateRole,
  };
};
